import { Link, Outlet, useNavigate } from "react-router-dom";
import { AdminSidebarNav } from "@/components/admin/AdminSidebarNav";
import { useAdminAuth } from "@/lib/auth/admin-auth-context";

/**
 * Layout de tout le back-office admin (`/admin/**`) — porté depuis
 * apps/web/app/(admin)/admin/layout.tsx, avec un écart assumé par rapport à
 * l'original (cf. rapport de la tâche de scaffolding) : contrairement à
 * apps/web (où ce layout est imbriqué SOUS le layout racine boutique, donc
 * affiche quand même header/footer/tab bar boutique autour de la sidebar
 * admin), ici c'est un layout de routes top-level INDÉPENDANT de
 * `ShopLayout` (aucun chrome boutique) — cohérent avec la description de la
 * tâche ("trois layouts top-level") et avec la pratique normale d'un
 * back-office. Pas de `<html>`/`<body>` ici (posés une seule fois dans
 * index.html).
 *
 * La garde d'accès réelle (redirection si non connecté) est
 * `@/components/admin/AdminRouteGuard`, appliquée route par route dans
 * router.tsx — PAS ici : ce layout enveloppe aussi `/admin/connexion`
 * lui-même, une redirection inconditionnelle ici y boucherait indéfiniment.
 * Comme l'original, ce layout se contente de ne PAS afficher le cadre
 * sidebar/déconnexion tant qu'aucune session n'est détectée (rendu nu, pour
 * `/admin/connexion`).
 */
export function AdminLayout() {
  const { admin, isLoggedIn, logout } = useAdminAuth();
  const navigate = useNavigate();

  if (!isLoggedIn || !admin) {
    return <Outlet />;
  }

  const displayName = [admin.firstName, admin.lastName].filter(Boolean).join(" ") || admin.email;

  function handleLogout() {
    logout();
    navigate("/admin/connexion", { replace: true });
  }

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <aside className="flex shrink-0 flex-col justify-between bg-navy px-4 py-6 lg:w-64 lg:px-5 lg:py-8">
        <div>
          <Link to="/admin" className="block" aria-label="VVS admin — retour au tableau de bord">
            <img src="/logo-footer.png" alt="VVS — Vendez Vouse Store" width={160} height={115} className="h-10 w-auto" />
          </Link>

          <div className="mt-6">
            <AdminSidebarNav />
          </div>
        </div>

        <div className="mt-6 border-t border-white/10 pt-4">
          <p className="truncate text-sm font-medium text-cream">{displayName}</p>
          <p className="truncate text-xs text-cream2">{admin.email}</p>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-3 w-full rounded-sm border border-white/20 px-3 py-2 text-left text-sm font-medium text-cream hover:bg-white/10"
          >
            Se déconnecter
          </button>
        </div>
      </aside>

      <main className="flex-1 bg-cream px-4 py-6 lg:px-8 lg:py-8">
        <Outlet />
      </main>
    </div>
  );
}
